import { type AgentSubscriber, type HttpAgent, type RunAgentParameters } from '@ag-ui/client';
import { type Message, type UserMessage } from '@ag-ui/core';
import { v4 as uuid } from 'uuid';

import { reportErrorToBugsnag } from '@air/utils-error';

import { BrandKitExpandingHttpAgent } from './brandKitExpandingHttpAgent';
import { EventSubscriptionManager } from './EventSubscriptionManager';
import { isAbortError, isHttpError } from './errors';
import { sanitizeMediaMap } from './sanitizeMediaMap';
import { type AgentState, type ThreadData } from './store/slices/createChatSlice';
import { useRunErrorStore } from './store/useRunErrorStore';
import { useToolCallStatusStore } from './store/useToolCallStatusStore';
import { extractMediaReferenceIds } from './tokens/tokenSerialization';
import { ALL_TOOLS, isFileEditCompleteResult, isGenerateTextResult, validateToolCallResult } from './tools';
import {
  type GetAuthToken,
  type ProgrammaticMessageOptions,
  type RequestedVariantCount,
  type RunSettledOutcome,
  type SendMessageParams,
  type StallTrigger,
} from './types/subscriptions';

export type AgentManagerConfig = {
  agentUrl: string;
  getAuthToken: GetAuthToken;
  getThreadData: (threadId: string) => ThreadData | null;
  onMessagesChanged: (threadId: string, messages: Message[]) => void;
  onStateChanged: (threadId: string, state: AgentState) => void;
  resourceId: string;
  workspaceId: string;
};

export type RunningStateListener = () => void;

export class AgentBusyError extends Error {
  constructor() {
    super('The agent is already working on your previous request.');
    this.name = 'AgentBusyError';
  }
}

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const DEFAULT_EMPTY_STATE: AgentState = null;
const DEFAULT_EMPTY_MESSAGES: Message[] = [];

// The backend emits a synthetic assistant message on cancellation that should never surface in the UI.
const isSyntheticCancelMessage = (m: Message) => m.role === 'assistant' && m.content === 'Cancelled by user';

const toVisibleMessages = (messages: Message[]): Message[] =>
  messages.filter((msg) => msg.role !== 'system').filter((msg) => !isSyntheticCancelMessage(msg));

// RunHttpAgentConfig isn't re-exported by @ag-ui/client but HttpAgent.runAgent() reads abortController from params at runtime.
type RunHttpAgentConfig = RunAgentParameters & { abortController?: AbortController };

/**
 * Why a thread's in-flight run was intentionally aborted — the value picks the cleanup:
 * 'cancel' = user stop (roll back messages, restore composer, suppress errors);
 * 'abandon' = transport failure (keep everything delivered, just stop waiting).
 */
type AbortIntent = 'cancel' | 'abandon';

// Heartbeat CUSTOM events arrive every ~10–15s during silent stretches and every frame
// resets the idle clock — 45s of silence is several missed heartbeats: dead, not quiet.
const IDLE_TIMEOUT_MS = 45_000;

// RUN_FINISHED means the server is done; if EOF doesn't follow within the grace,
// force-settle instead of waiting on a close that will never come.
const CLOSE_OUT_GRACE_MS = 4_000;

// Retryable transport failure. Written only for runs that ended without a terminal event
// AND produced no client-side result — so a retry cannot duplicate a delivered node.
// Rendered by StreamIssueAlert, which owns the display copy; this message is the fallback.
const STREAM_ISSUE_RUN_ERROR = {
  code: 'STREAM_ISSUE',
  message: 'This task was taking longer than expected, and we stopped it.',
};

// Written when a retry bounces off the still-held server lock: the escalated cancel is
// cooperative and only lands at the next tool-call checkpoint, which a long in-flight
// generation can hold off for minutes. Re-arming the stream-issue surface keeps the
// retry affordance alive instead of decaying into the generic AGENT_BUSY banner.
const STREAM_ISSUE_RETRY_BUSY_RUN_ERROR = {
  code: 'STREAM_ISSUE_RETRY_BUSY',
  message: "We're still winding down the previous attempt — this can take a few minutes.",
};

/**
 * Per-run health, keyed by threadId (the busy-guard enforces one run per thread): created
 * on RUN_STARTED, deleted at settle or agent removal. settleRun() classifies the run's
 * exit from these fields.
 */
type RunHealth = {
  startedAt: number;
  /** `performance.now()` of the run's last stream frame; starts at `startedAt`. */
  trafficAt: number;
  runId?: string;
  actionName?: string;
  sourceNodeId?: string;
  /** RUN_FINISHED received — the deliverable is complete even if the stream never closes. */
  runFinishedReceived: boolean;
  /** A node-materializing tool result landed — gates the stream-issue notice (no result ⇒ Retry is safe). */
  resultArrived: boolean;
  /** A genuine terminal error surfaced (intentional-abort synthetics excluded). */
  errorSeen: boolean;
  idleTimer?: ReturnType<typeof setTimeout>;
  closeOutTimer?: ReturnType<typeof setTimeout>;
  requestedVariantCount?: RequestedVariantCount;
  /** `file_edit_complete` edits delivered so far. */
  deliveredVariantCount: number;
};

const collectRunSourceIds = (
  messages: readonly Message[],
  programmaticOptions: ProgrammaticMessageOptions | undefined,
): string[] => {
  const ids = new Set<string>();
  const programmaticSourceId = programmaticOptions?.sourceNodeId ?? programmaticOptions?.sourceNode?.group.id;

  if (programmaticSourceId) {
    ids.add(programmaticSourceId);
  }

  for (const message of messages) {
    if (typeof message.content === 'string') {
      for (const id of extractMediaReferenceIds(message.content)) {
        ids.add(id);
      }
    }
  }

  return [...ids];
};

export class AgentManager {
  private agents = new Map<string, HttpAgent>();
  private subscriptions = new Map<string, { unsubscribe: () => void }>();
  private programmaticOptions = new Map<string, ProgrammaticMessageOptions>();
  /** Analytics only — the count itself travels in the prompt text. */
  private readonly requestedVariantCounts = new Map<string, RequestedVariantCount>();
  private readonly threadsAwaitingReply = new Set<string>();
  private runningThreads = new Set<string>();
  private runningStateListeners = new Set<RunningStateListener>();
  private pendingBusyErrors = new Set<string>();
  /** Threads whose current run was aborted on purpose — the value picks the cleanup. */
  private abortIntent = new Map<string, AbortIntent>();
  private runControllers = new Map<string, AbortController>();
  private lastSentMessages = new Map<string, string>();
  /** Source ids for the in-flight send only — not leftover lastSentMessages from a prior chat. */
  private runSourceIds = new Map<string, string[]>();
  private lastKnownMessages = new Map<string, Message[]>();
  private preRunMessages = new Map<string, Message[]>();
  /**
   * Last accepted send payload per thread, replayed by retryRun(). Deliberately NOT
   * cleared in removeAgent() — retryRun's own revert evicts the agent and the payload
   * must survive that eviction. Only dispose() clears the map.
   */
  private lastSendParams = new Map<string, SendMessageParams>();
  /** Health of each thread's in-flight run, from RUN_STARTED to settle. */
  private runHealth = new Map<string, RunHealth>();
  private config: AgentManagerConfig;
  /** Cached so cancelRun() can fire the cancel POST without an extra token fetch. */
  private lastAuthToken: string | null = null;

  public readonly eventSubscriptions = new EventSubscriptionManager();

  constructor(config: AgentManagerConfig) {
    this.config = config;
  }

  /** Update the resource (canvas) ID without recreating the manager. Used during draft→real canvas commit. */
  setResourceId(resourceId: string): void {
    this.config = { ...this.config, resourceId };
  }

  subscribeToRunningState(listener: RunningStateListener): () => void {
    this.runningStateListeners.add(listener);
    return () => {
      this.runningStateListeners.delete(listener);
    };
  }

  private notifyRunningStateChange(): void {
    // Snapshot to protect against modifications during iteration
    const listeners = Array.from(this.runningStateListeners);
    for (const listener of listeners) {
      listener();
    }
  }

  getAgent(threadId: string): HttpAgent {
    const existing = this.agents.get(threadId);

    if (existing) {
      return existing;
    }

    const threadData = this.config.getThreadData(threadId);

    // Neither synthetic cancel markers nor system messages (programmatic run prompts) belong
    // in the agent's replayed history or the internal snapshot.
    const cleanMessages = (threadData?.messages ?? DEFAULT_EMPTY_MESSAGES).filter(
      (m) => !isSyntheticCancelMessage(m) && m.role !== 'system',
    );

    const agent = new BrandKitExpandingHttpAgent({
      headers: {
        'x-amzn-bedrock-agentcore-runtime-session-id': threadId,
      },
      initialMessages: cleanMessages,
      initialState: threadData?.state ?? DEFAULT_EMPTY_STATE,
      threadId,
      url: this.config.agentUrl,
    });

    this.agents.set(threadId, agent);
    this.lastKnownMessages.set(threadId, cleanMessages);
    this.subscribeToAgent(threadId, agent);

    return agent;
  }

  removeAgent(threadId: string): void {
    const subscription = this.subscriptions.get(threadId);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(threadId);
    }

    this.agents.delete(threadId);
    this.runningThreads.delete(threadId);
    this.abortIntent.delete(threadId);
    this.runControllers.delete(threadId);
    this.programmaticOptions.delete(threadId);
    this.requestedVariantCounts.delete(threadId);
    this.threadsAwaitingReply.delete(threadId);
    this.lastKnownMessages.delete(threadId);
    this.lastSentMessages.delete(threadId);
    this.runSourceIds.delete(threadId);
    this.preRunMessages.delete(threadId);
    this.pendingBusyErrors.delete(threadId);
    this.clearRunHealth(threadId);
  }

  isRunning(threadId: string): boolean {
    return this.runningThreads.has(threadId);
  }

  /**
   * Node ids this in-flight send is using: programmatic source plus `[media:]` mentions
   * in this send's messages. Snapshot at send start so a prior chat mention cannot
   * cancel a later transform. Empty when the thread is not running, or when a
   * node-materializing result has already landed — ToolCallResultWatcher then removes
   * the ephemeral `imageGeneration` placeholder, which must not look like a user abort.
   */
  getInFlightRunSourceIds(threadId: string): string[] {
    if (!this.runningThreads.has(threadId) || this.runHealth.get(threadId)?.resultArrived) {
      return [];
    }

    return this.runSourceIds.get(threadId) ?? [];
  }

  /** Adds a user message to thread history without starting an agent run. */
  appendUserMessage(threadId: string, content: string): void {
    const agent = this.getAgent(threadId);
    const userMessage: UserMessage = {
      content,
      id: `msg_${uuid()}`,
      role: 'user',
    };

    agent.addMessage(userMessage);

    const visibleMessages = toVisibleMessages(agent.messages);
    this.lastKnownMessages.set(threadId, visibleMessages);
    this.config.onMessagesChanged(threadId, visibleMessages);
    this.eventSubscriptions.notifyMessagesChanged(threadId, visibleMessages);
  }

  getVariantCountAwaitingReply(threadId: string): RequestedVariantCount | undefined {
    return this.threadsAwaitingReply.has(threadId) ? this.requestedVariantCounts.get(threadId) : undefined;
  }

  async sendMessage(params: SendMessageParams): Promise<void> {
    const { threadId, messages, context, programmaticOptions, variantCount } = params;

    if (this.runningThreads.has(threadId) || this.abortIntent.get(threadId) === 'cancel') {
      throw new AgentBusyError();
    }

    // Cached only after the busy-guard passes — a rejected send must not clobber
    // the last real payload that retryRun() would replay.
    this.lastSendParams.set(threadId, params);

    const isToolbarAction = Boolean(programmaticOptions?.actionName);
    // Any send answers (or supersedes) a pending agent question.
    const wasAwaitingReply = this.threadsAwaitingReply.delete(threadId);
    // Only a typed user reply carries the count forward; a toolbar send is a different action.
    const isReplyToQuestion = wasAwaitingReply && messages[0]?.role === 'user' && !isToolbarAction;

    if (variantCount) {
      this.requestedVariantCounts.set(threadId, variantCount);
    } else if (messages[0]?.role !== 'tool' && !isReplyToQuestion) {
      this.requestedVariantCounts.delete(threadId);
    }

    const agent = this.getAgent(threadId);

    useRunErrorStore.getState().clearRunError(threadId);

    try {
      this.runningThreads.add(threadId);
      if (programmaticOptions) {
        this.programmaticOptions.set(threadId, programmaticOptions);
      }
      // A toolbar action's generated text must not be restored into the composer on cancel.
      if (isToolbarAction) {
        this.lastSentMessages.delete(threadId);
      }
      this.runSourceIds.set(threadId, collectRunSourceIds(messages, programmaticOptions));
      this.notifyRunningStateChange();

      // Snapshot messages before adding new ones so we can revert if the agent is busy
      this.preRunMessages.set(threadId, this.lastKnownMessages.get(threadId) ?? []);

      for (const message of messages) {
        agent.addMessage(message);
        if (!isToolbarAction && message.role === 'user' && typeof message.content === 'string') {
          this.lastSentMessages.set(threadId, message.content);
        }
      }

      agent.setState({
        ...agent.state,
        canvasId: this.config.resourceId,
        workspaceId: this.config.workspaceId,
      });

      // Create the AbortController before the async getAuthToken() gap so that
      // cancelRun() fired during token fetch aborts the correct controller.
      const abortController = new AbortController();
      this.runControllers.set(threadId, abortController);

      const runConfig: RunHttpAgentConfig = { abortController, context, tools: ALL_TOOLS };

      const token = await this.config.getAuthToken();
      this.lastAuthToken = token;
      agent.headers.Authorization = `Bearer ${token}`;

      try {
        await agent.runAgent(runConfig);
      } catch (error) {
        // Every controller on this promise is ours, so an AbortError here is always our own
        // cancel or abandon, whatever state the abortIntent bookkeeping raced into.
        if (this.settleIntentionalAbort(threadId) || isAbortError(error)) {
          return;
        }
        if (isHttpError(error) && (error.status === HTTP_UNAUTHORIZED || error.status === HTTP_FORBIDDEN)) {
          // Token may have expired between fetch and request
          const freshToken = await this.config.getAuthToken({ forceRefresh: true });
          this.lastAuthToken = freshToken;
          agent.headers.Authorization = `Bearer ${freshToken}`;

          try {
            await agent.runAgent(runConfig);
          } catch (retryError) {
            if (this.settleIntentionalAbort(threadId) || isAbortError(retryError)) {
              return;
            }
            throw retryError;
          }
        } else {
          throw error;
        }
      }

      // runAgent() completed normally — backend may have sent RUN_FINISHED after its own
      // rollback, so still revert frontend messages if cancel was requested.
      if (this.settleIntentionalAbort(threadId)) {
        return;
      }

      if (this.pendingBusyErrors.has(threadId)) {
        this.revertToPreRunSnapshot(threadId);
        throw new AgentBusyError();
      }
    } finally {
      this.runControllers.delete(threadId);
      this.runningThreads.delete(threadId);
      this.programmaticOptions.delete(threadId);
      this.runSourceIds.delete(threadId);
      // A busy bounce is only meaningful to the run that received it — clear on every
      // exit so an abandoned run can't leak its entry into the next send's check above.
      this.pendingBusyErrors.delete(threadId);
      // Normally a no-op (the stream's finalize already ran settleRun, clearing health) —
      // a belt for that ordering assumption: a run that somehow exits without finalizing
      // still settles here instead of recovery dying silently. Must run before the
      // abortIntent delete so classification can read the intent.
      this.settleRun(threadId);
      this.abortIntent.delete(threadId);
      this.notifyRunningStateChange();
    }
  }

  /**
   * Shared run-exit handling for intentional aborts, whether runAgent rejected (the abort)
   * or resolved (RUN_FINISHED raced it). A cancel rolls back only after runAgent settles —
   * rolling back earlier races HttpAgent's message events. Returns true when the exit was
   * an intentional abort.
   */
  private settleIntentionalAbort(threadId: string): boolean {
    const intent = this.abortIntent.get(threadId);

    if (intent === 'cancel') {
      this.rollbackCancelledRun(threadId);
    }

    return intent !== undefined;
  }

  /**
   * Replays the thread's last send after a transport failure. Safe to fire blind: a
   * STREAM_ISSUE notice only exists for runs that produced no result (enforced at write
   * time), and settleRun already fired the backend cancel that frees the server lock.
   */
  async retryRun(threadId: string): Promise<void> {
    const cachedParams = this.lastSendParams.get(threadId);

    // The payload/running guards are defensive; the cancel check IS reachable (Stop
    // clicked, abort still settling) — a retry in that window would removeAgent → drop
    // the abort intent, un-swallowing the AbortError and letting the original send's
    // finally clobber the replayed run's state.
    if (!cachedParams || this.runningThreads.has(threadId) || this.abortIntent.get(threadId) === 'cancel') {
      return;
    }

    // Chat parity: the server rolled back its side when the cancel landed, so drop the
    // stale half-exchange before resending. This evicts the agent; the cached payload
    // survives eviction.
    this.revertToPreRunSnapshot(threadId);

    // Send through the normal pipeline so run-health tracking treats the retry as a fresh
    // run; its synchronous clearRunError drops the notice on click.
    try {
      await this.sendMessage(cachedParams);
    } catch (error) {
      // The guards above return early for every client-side busy case, so busy here means
      // the server bounce: the lock is still held mid-cancel. The busy rollback already
      // reverted the replay, so re-arming the notice keeps the retry button a safe probe.
      if (error instanceof AgentBusyError) {
        useRunErrorStore.getState().setRunError(threadId, STREAM_ISSUE_RETRY_BUSY_RUN_ERROR);
        return;
      }

      throw error;
    }
  }

  cancelRun(threadId: string): string | null {
    // No active run — bail out to avoid a stale cancel intent that would block future sends.
    if (!this.runControllers.has(threadId)) {
      return this.lastSentMessages.get(threadId) ?? null;
    }

    // A cancel overrides a pending abandon: user intent wins, its rollback included.
    this.abortIntent.set(threadId, 'cancel');

    this.runningThreads.delete(threadId);
    this.notifyRunningStateChange();
    useToolCallStatusStore.getState().failToolCalls(threadId);

    const controller = this.runControllers.get(threadId);
    const token = this.lastAuthToken;

    controller?.abort();

    if (token) {
      // User-initiated Stop: a failed cancel is surfaced — the backend may still be running
      this.sendCancelRequest(threadId, token).catch(() => {
        this.handleRunError(threadId, {
          code: 'CANCEL_FAILED',
          message: 'Stop request failed to reach the server. Backend execution may still be active.',
        });
      });
    }

    return this.lastSentMessages.get(threadId) ?? null;
  }

  /**
   * Stop waiting on a dead/hung stream without discarding anything already delivered.
   * Unlike cancelRun (user intent), this performs no message rollback, no composer
   * restore, and no backend cancel POST.
   */
  private abandonRun(threadId: string): void {
    const controller = this.runControllers.get(threadId);

    // No active run — bail out to avoid a stale abandon intent poisoning a later run.
    if (!controller) {
      return;
    }

    // A user cancel already in flight wins — don't demote its rollback to an abandon.
    if (this.abortIntent.has(threadId)) {
      return;
    }

    // Mark before aborting — the synthetic RUN_ERROR (code 'abort') can fire synchronously.
    this.abortIntent.set(threadId, 'abandon');
    controller.abort();
  }

  /**
   * Background cancel POST for a run whose stream we already gave up on — tells the
   * server to stop and free its lock (it may still be mid-run). Touches no run-tracking
   * state and performs no rollback. Failures are Bugsnag-only: the user never initiated
   * this cancel, and a genuinely held lock resurfaces through the retry bounce instead.
   */
  private escalateCancel(threadId: string): void {
    // No cached token means no run was ever sent from this session — nothing to cancel.
    if (!this.lastAuthToken) {
      return;
    }

    // Fetch a fresh token rather than reusing the send-time cache, which may be stale by
    // the time the escalation fires — a 401 here would silently leave the server lock held.
    this.config
      .getAuthToken()
      .then((token) => this.sendCancelRequest(threadId, token))
      .catch((error: unknown) => {
        reportErrorToBugsnag({
          context: 'Background agent-run cancel escalation failed',
          error,
          metadata: { threadId },
        });
      });
  }

  /**
   * Re-evaluate every in-flight run's idle window right now. In-page timers under-fire in
   * hidden/suspended tabs (the laptop-sleep case), so the browser glue calls this on
   * tab-return and network reconnect.
   */
  sweepStalledRuns(trigger: Exclude<StallTrigger, 'timer'>): void {
    for (const threadId of Array.from(this.runHealth.keys())) {
      this.fireWatchdog(threadId, trigger);
    }
  }

  dispose(): void {
    for (const threadId of this.agents.keys()) {
      this.removeAgent(threadId);
    }
    this.lastSendParams.clear();
    this.eventSubscriptions.dispose();
  }

  /**
   * Fire the backend cancel POST. Rejects on network failure or a non-ok response —
   * failure policy lives with the callers, so every caller must attach a .catch.
   */
  private async sendCancelRequest(threadId: string, token: string): Promise<void> {
    const response = await fetch(this.config.agentUrl, {
      body: JSON.stringify({
        messages: [],
        state: { cancel: 'true', canvasId: this.config.resourceId, workspaceId: this.config.workspaceId },
        threadId,
        tools: [],
      }),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-amzn-bedrock-agentcore-runtime-session-id': threadId,
      },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`Cancel request responded with ${response.status}`);
    }
  }

  private handleRunError(threadId: string, error: { message: string; code?: string }): void {
    useRunErrorStore.getState().setRunError(threadId, error);
    useToolCallStatusStore.getState().failToolCalls(threadId);
  }

  private handleRunFailure(threadId: string, error: Error): void {
    this.handleRunError(threadId, {
      code: 'UNKNOWN',
      message: error.message,
    });
  }

  private revertToPreRunSnapshot(threadId: string): void {
    const snapshot = this.preRunMessages.get(threadId);

    // A missing snapshot is NOT an empty thread — the last send's own rollback already
    // consumed it (e.g. retry after a bounced retry), so the store already holds the
    // pre-send state. An empty fallback here would blank the thread's history.
    if (snapshot) {
      this.config.onMessagesChanged(threadId, snapshot);
      this.preRunMessages.delete(threadId);
    }

    // The rollback takes the run's tool chips with its messages — orphaned entries would
    // linger as ghost synthetic chips (see useMessages)
    useToolCallStatusStore.getState().clearToolCalls(threadId);

    this.removeAgent(threadId);
  }

  private rollbackCancelledRun(threadId: string): void {
    this.revertToPreRunSnapshot(threadId);
    this.abortIntent.delete(threadId);
  }

  private clearRunHealth(threadId: string): void {
    const health = this.runHealth.get(threadId);

    if (health) {
      clearTimeout(health.idleTimer);
      clearTimeout(health.closeOutTimer);
      this.runHealth.delete(threadId);
    }
  }

  private markRunErrored(threadId: string): void {
    const health = this.runHealth.get(threadId);

    if (health) {
      health.errorSeen = true;
    }
  }

  private beginRunHealth(threadId: string, runId: string): void {
    // Overwriting a stale entry must not leak its timers
    this.clearRunHealth(threadId);

    const startedAt = performance.now();
    const programmaticOptions = this.programmaticOptions.get(threadId);
    const health: RunHealth = {
      actionName: programmaticOptions?.actionName,
      deliveredVariantCount: 0,
      errorSeen: false,
      requestedVariantCount: this.requestedVariantCounts.get(threadId),
      resultArrived: false,
      runFinishedReceived: false,
      runId,
      sourceNodeId: programmaticOptions?.sourceNodeId ?? programmaticOptions?.sourceNode?.group.id,
      startedAt,
      trafficAt: startedAt,
    };

    this.runHealth.set(threadId, health);
    health.idleTimer = setTimeout(() => this.fireWatchdog(threadId, 'timer'), IDLE_TIMEOUT_MS);

    this.eventSubscriptions.notifyRunLifecycle({
      actionName: health.actionName,
      phase: 'started',
      runId,
      sourceNodeId: health.sourceNodeId,
      threadId,
    });
  }

  /**
   * Shared watchdog path — the idle timer, its re-arms, and the tab-return/reconnect
   * sweeps all land here, so every trigger produces the same beacon + recovery sequence.
   * Pull-based: token streams fire many frames per second, so re-arming a timer per frame
   * would thrash — onEvent only stamps the traffic clock, and the timer re-arms itself for
   * the remainder when it finds fresh traffic. Sweeps leave the run's own timer in flight.
   */
  private fireWatchdog(threadId: string, trigger: StallTrigger): void {
    const health = this.runHealth.get(threadId);

    // The abort-intent check is both the re-entry guard (the stall path's own abandonRun
    // sets 'abandon' synchronously) and the cancel guard (a Stop clicked inside the idle
    // window is already settling — a stalled beacon for it would be telemetry fiction).
    if (!health || this.abortIntent.has(threadId)) {
      return;
    }

    const elapsedSinceTraffic = performance.now() - health.trafficAt;

    if (elapsedSinceTraffic < IDLE_TIMEOUT_MS) {
      if (trigger === 'timer') {
        health.idleTimer = setTimeout(
          () => this.fireWatchdog(threadId, 'timer'),
          IDLE_TIMEOUT_MS - elapsedSinceTraffic,
        );
      }

      return;
    }

    if (health.runFinishedReceived) {
      // Finished-but-unclosed stream: close-out semantics, not a stall — no beacon, and
      // settleRun keeps the `finished` outcome. Happens when RUN_FINISHED landed in a
      // hidden tab and the close-out timer was throttled until the user returned.
      clearTimeout(health.closeOutTimer);
      this.abandonRun(threadId);

      return;
    }

    // A sweep can beat the run's own idle timer — clear it so it doesn't fire pointlessly
    clearTimeout(health.idleTimer);
    health.idleTimer = undefined;

    this.eventSubscriptions.notifyRunLifecycle({
      actionName: health.actionName,
      elapsedMs: Math.round(performance.now() - health.startedAt),
      phase: 'stalled',
      resultArrived: health.resultArrived,
      runFinishedReceived: health.runFinishedReceived,
      runId: health.runId,
      sourceNodeId: health.sourceNodeId,
      threadId,
      trigger,
    });

    // Fire = detect + kill; settle = report + recover (settleRun writes the notice and
    // escalates once the run has actually settled). Only the beacon fires pre-recovery,
    // so telemetry survives an abort that never settles.
    this.abandonRun(threadId);
  }

  /**
   * Run-exit accounting, keyed off stream finalize — the one signal shared by every way a
   * run can end (EOF, server error, user cancel, forced abandon). Classifies the outcome,
   * emits the settled lifecycle event, and runs transport-failure recovery when the run
   * ended without a terminal event.
   */
  private settleRun(threadId: string): void {
    const health = this.runHealth.get(threadId);

    // A finalize with no tracked run means the stream failed before RUN_STARTED (e.g. the
    // auth-retry's first attempt): nothing became a run, so there is nothing to classify.
    if (!health) {
      return;
    }

    this.clearRunHealth(threadId);

    // No chip outlives its run: a tool call still "working" at settle can never complete
    // (its result event can no longer arrive). No-op for healthy runs.
    useToolCallStatusStore.getState().failToolCalls(threadId);

    let outcome: RunSettledOutcome;

    if (health.runFinishedReceived) {
      // The deliverable is complete even if the stream tail had to be force-closed
      outcome = 'finished';
    } else if (this.abortIntent.get(threadId) === 'cancel') {
      outcome = 'aborted';
    } else if (health.errorSeen) {
      outcome = 'errored';
    } else {
      // Watchdog force-abandon or spontaneous truncation: ended without a terminal event
      outcome = 'abandoned';
    }

    if (outcome === 'finished' && !health.resultArrived) {
      this.threadsAwaitingReply.add(threadId);
    }

    this.eventSubscriptions.notifyRunLifecycle({
      actionName: health.actionName,
      deliveredVariantCount: health.deliveredVariantCount,
      durationMs: Math.round(performance.now() - health.startedAt),
      outcome,
      phase: 'settled',
      requestedVariantCount: health.requestedVariantCount?.count,
      resultArrived: health.resultArrived,
      runFinishedReceived: health.runFinishedReceived,
      runId: health.runId,
      sourceNodeId: health.sourceNodeId,
      threadId,
      variantCountSource: health.requestedVariantCount?.source,
    });

    if (outcome === 'abandoned') {
      // Died-after-result recovers silently — the artifact landed, and a retry would risk
      // duplicating it. Only no-result failures get the notice.
      if (!health.resultArrived) {
        useRunErrorStore.getState().setRunError(threadId, STREAM_ISSUE_RUN_ERROR);
      }

      // The server may still be mid-run — free its lock so a retry starts cleanly
      this.escalateCancel(threadId);
    }
  }

  /**
   * Single subscriber wiring all agent events: chat/state/tool-call handling plus the
   * run-health accounting that drives the run-lifecycle events on `eventSubscriptions`.
   */
  private subscribeToAgent(threadId: string, agent: HttpAgent): void {
    const subscriber: AgentSubscriber = {
      // Every stream frame — including heartbeat CUSTOM events — proves the stream is
      // alive. Frames before RUN_STARTED don't stamp: no watchdog is armed yet.
      onEvent: () => {
        const health = this.runHealth.get(threadId);

        if (health) {
          health.trafficAt = performance.now();
        }
      },
      onMessagesChanged: (params) => {
        if (this.abortIntent.get(threadId) === 'cancel') {
          return;
        }
        // Synthetic cancel markers are internal and never belong in any downstream consumer.
        const cancelFreeMessages = params.messages.filter((msg) => !isSyntheticCancelMessage(msg));
        const visibleMessages = toVisibleMessages(params.messages);
        this.lastKnownMessages.set(threadId, visibleMessages);
        // The store keeps system messages (programmatic run prompts) so useMessages can use them
        // as run boundaries; useMessages filters them out of display. Internal bookkeeping
        // (lastKnownMessages) and non-display event subscribers stay system-free.
        this.config.onMessagesChanged(threadId, cancelFreeMessages);
        this.eventSubscriptions.notifyMessagesChanged(threadId, visibleMessages);
      },
      onRunErrorEvent: (params) => {
        // Intentional-abort synthetics don't count as "the run errored" — settleRun
        // classifies those from abortIntent instead
        if (this.abortIntent.has(threadId)) {
          return;
        }

        this.markRunErrored(threadId);

        // Routed through the busy-flow rollback in sendMessage (revert messages,
        // evict agent, throw AgentBusyError → AGENT_BUSY UI) instead of the
        // generic run-error path so the user can recover by starting a new thread.
        if (params.event.code === 'CONCURRENT_INVOCATION') {
          this.pendingBusyErrors.add(threadId);
        } else {
          this.handleRunError(threadId, {
            code: params.event.code,
            message: params.event.message,
          });
        }
      },
      onRunFailed: (params) => {
        if (this.abortIntent.has(threadId)) {
          return;
        }

        this.markRunErrored(threadId);
        this.handleRunFailure(threadId, params.error);
      },
      onRunFinalized: () => this.settleRun(threadId),
      onRunFinishedEvent: () => {
        const health = this.runHealth.get(threadId);

        if (!health) {
          return;
        }

        health.runFinishedReceived = true;

        // Close-out-on-finish: the server is done, so a stream still open past the grace
        // will never close — abandon so the run settles. No beacon, no notice, no
        // escalation (the run succeeded). Healthy streams settle first and clear this.
        clearTimeout(health.closeOutTimer);
        health.closeOutTimer = setTimeout(() => this.abandonRun(threadId), CLOSE_OUT_GRACE_MS);
      },
      onRunStartedEvent: (params) => {
        this.beginRunHealth(threadId, params.event.runId);
      },
      onStateChanged: (params) => {
        if (this.abortIntent.get(threadId) === 'cancel') {
          return;
        }
        const state = params.state?.mediaMap
          ? { ...params.state, mediaMap: sanitizeMediaMap(params.state.mediaMap) }
          : params.state;
        this.config.onStateChanged(threadId, state);
        this.eventSubscriptions.notifyStateChanged(threadId, state);
      },
      onToolCallResultEvent: (params) => {
        if (this.abortIntent.get(threadId) === 'cancel') {
          return;
        }
        useToolCallStatusStore.getState().completeToolCall(threadId, params.event.toolCallId);

        const validationResult = validateToolCallResult(params.event.toolCallId, params.event.content, params.messages);

        if (validationResult.success) {
          // Only node-materializing results count as the deliverable — intermediate probes
          // like detect_text must not read as "result landed" in the stream-issue gate
          if (isFileEditCompleteResult(validationResult.params) || isGenerateTextResult(validationResult.params)) {
            const health = this.runHealth.get(threadId);

            if (health) {
              health.resultArrived = true;

              if (isFileEditCompleteResult(validationResult.params)) {
                health.deliveredVariantCount += validationResult.params.toolCallResult.edits.length;
              }
            }
          }

          this.eventSubscriptions.notifyToolCallResult(threadId, {
            event: params.event,
            programmaticOptions: this.programmaticOptions.get(threadId),
            ...validationResult.params,
          });
        }
      },
      onToolCallStartEvent: (params) => {
        if (this.abortIntent.get(threadId) === 'cancel') {
          return;
        }
        useToolCallStatusStore.getState().startToolCall(threadId, params.event.toolCallId, params.event.toolCallName);
      },
    };

    const subscription = agent.subscribe(subscriber);
    this.subscriptions.set(threadId, subscription);
  }
}
